/*
 * rheincool — Cookie-Zustimmung (Cookie-Consent-Handoff v1.0)
 * Quelle der Wahrheit: Shopify Customer Privacy API
 * (setTrackingConsent — Marketing-/Analytics-Pixel hängen daran).
 * Eigenes Consent-Cookie: Version + Zeitstempel + Auswahl, Laufzeit 6 Monate;
 * bei Textänderung (CONSENT_VERSION) wird erneut gefragt.
 * Ebene 1 ohne ✕ und ohne Klick-außerhalb; Escape schließt nur Ebene 2.
 */
(() => {
  'use strict';
  if (window.__rcConsent) return;
  window.__rcConsent = true;

  const CONSENT_VERSION = 1;
  const COOKIE = 'rc_consent';
  const MAX_AGE = 15552000; // 6 Monate

  const root = document.querySelector('[data-consent]');
  if (!root) return;
  const banner = root.querySelector('[data-consent-banner]');
  const modal = root.querySelector('[data-consent-modal]');
  const fab = document.querySelector('[data-consent-fab]');
  const toast = document.querySelector('[data-consent-toast]');
  let toastTimer = null;
  let lastFocused = null;

  const track = (event, params) => {
    if (window.rcTrack) window.rcTrack(event, params || {});
  };

  /* Der Schild-Knopf erscheint erst nach etwa einem Bildschirm Scrolltiefe
     (Haendlerwunsch 01.09.2026): beim Seitenaufruf lag er auf Bild oder
     Titel, dem Ersteindruck der neuen Bild-zuerst-Produktseite. Rechtlich
     unkritisch: der Knopf existiert weiter auf jeder Seite (Art. 7 Abs. 3
     DSGVO — Widerruf so einfach wie Erteilung), dazu der Footer-Link.
     `hidden` steuert weiterhin, OB er gezeigt werden darf (erst nach einer
     Einwilligungswahl); die Klasse steuert nur das WANN im Viewport. */
  const fabFreigeben = () => {
    if (!fab) return;
    fab.hidden = false;
    if (fab.dataset.scrollGebunden) return;
    fab.dataset.scrollGebunden = '1';
    const pruefen = () => {
      fab.classList.toggle('is-da', window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener('scroll', pruefen, { passive: true });
    pruefen();
  };

  /* ---------- Cookie ---------- */

  const readConsent = () => {
    const match = document.cookie.match(/(?:^|; )rc_consent=([^;]*)/);
    if (!match) return null;
    try {
      const data = JSON.parse(decodeURIComponent(match[1]));
      if (data.v !== CONSENT_VERSION) return null;
      if (Date.now() - data.ts > MAX_AGE * 1000) return null;
      return data;
    } catch {
      return null;
    }
  };

  const writeConsent = (choices) => {
    const data = { v: CONSENT_VERSION, ts: Date.now(), ...choices };
    document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(data))}; path=/; max-age=${MAX_AGE}; SameSite=Lax; Secure`;
    return data;
  };

  /* ---------- Shopify Customer Privacy API ---------- */

  const withPrivacyApi = (callback) => {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      callback(window.Shopify.customerPrivacy);
      return;
    }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (!error && window.Shopify.customerPrivacy) callback(window.Shopify.customerPrivacy);
        }
      );
    }
  };

  const applyConsent = (choices) => {
    withPrivacyApi((api) => {
      api.setTrackingConsent(
        {
          analytics: Boolean(choices.analytics),
          marketing: Boolean(choices.marketing),
          preferences: Boolean(choices.preferences),
          sale_of_data: Boolean(choices.marketing),
        },
        () => {}
      );
    });
  };

  /* ---------- UI-Zustand ---------- */

  const toggles = () => Array.from(modal.querySelectorAll('[data-consent-toggle]'));

  const readToggles = () => {
    const choices = { analytics: false, preferences: false, marketing: false };
    toggles().forEach((toggle) => {
      const cat = toggle.dataset.consentToggle;
      if (cat !== 'necessary') choices[cat] = toggle.getAttribute('aria-checked') === 'true';
    });
    return choices;
  };

  const setToggles = (choices) => {
    toggles().forEach((toggle) => {
      const cat = toggle.dataset.consentToggle;
      if (cat === 'necessary') return;
      toggle.setAttribute('aria-checked', choices[cat] ? 'true' : 'false');
    });
  };

  const activeLabel = (choices) => {
    const names = [toast.dataset.necessaryLabel || 'Notwendig'];
    if (choices.analytics) names.push(toast.dataset.analyticsLabel || 'Statistik');
    if (choices.preferences) names.push(toast.dataset.preferencesLabel || 'Komfort');
    if (choices.marketing) names.push(toast.dataset.marketingLabel || 'Marketing');
    return names.join(' · ');
  };

  const showToast = (choices) => {
    toast.querySelector('[data-consent-toast-detail]').textContent = activeLabel(choices);
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.hidden = true;
    }, 4000);
  };

  const openBanner = () => {
    lastFocused = document.activeElement;
    root.hidden = false;
    banner.hidden = false;
    modal.hidden = true;
    document.body.classList.add('rc-no-scroll');
    banner.querySelector('#rc-consent-title').focus();
  };

  const openModal = (fromBanner) => {
    if (root.hidden) {
      lastFocused = document.activeElement;
      root.hidden = false;
      document.body.classList.add('rc-no-scroll');
    }
    const saved = readConsent();
    setToggles(saved || { analytics: false, preferences: false, marketing: false });
    banner.hidden = true;
    modal.hidden = false;
    modal.dataset.fromBanner = fromBanner ? 'true' : 'false';
    modal.querySelector('#rc-consent-settings-title').focus();
  };

  const closeModal = () => {
    // Escape/✕ auf Ebene 2: zurück zu Ebene 1 nur, wenn noch keine Entscheidung existiert
    if (modal.dataset.fromBanner === 'true' && !readConsent()) {
      modal.hidden = true;
      banner.hidden = false;
      banner.querySelector('#rc-consent-title').focus();
    } else {
      closeAll();
    }
  };

  const closeAll = () => {
    root.hidden = true;
    document.body.classList.remove('rc-no-scroll');
    fabFreigeben();
    if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
  };

  const decide = (choices) => {
    writeConsent(choices);
    applyConsent(choices);
    closeAll();
    showToast(choices);
  };

  /* ---------- Events ---------- */

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-consent-accept-all]')) {
      track('consent_decision', { auswahl: 'alle' });
      decide({ analytics: true, preferences: true, marketing: true });
    } else if (event.target.closest('[data-consent-necessary]')) {
      track('consent_decision', { auswahl: 'notwendig' });
      decide({ analytics: false, preferences: false, marketing: false });
    } else if (event.target.closest('[data-consent-save]')) {
      const choices = readToggles();
      track('consent_decision', { auswahl: 'individuell', ...choices });
      decide(choices);
    } else if (event.target.closest('[data-consent-open-settings]')) {
      openModal(true);
    } else if (event.target.closest('[data-consent-close]')) {
      closeModal();
    } else {
      const toggle = event.target.closest('[data-consent-toggle]');
      if (toggle && toggle.getAttribute('aria-disabled') !== 'true') {
        const next = toggle.getAttribute('aria-checked') !== 'true';
        toggle.setAttribute('aria-checked', next ? 'true' : 'false');
      }
    }
  });

  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) {
      event.preventDefault();
      closeModal();
    }
    if (event.key === 'Tab') {
      // Fokusfalle in der jeweils sichtbaren Ebene
      const scope = modal.hidden ? banner : modal;
      const focusables = scope.querySelectorAll('button, a[href], summary, [tabindex="-1"]');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  if (fab) {
    fab.addEventListener('click', () => {
      track('cookie_settings_reopen', { quelle: 'fingerprint' });
      openModal(false);
    });
  }

  // Footer-Link „Cookie-Einstellungen" (footer.js feuert dieses Event)
  document.addEventListener('rc:consent:open', () => openModal(false));

  /* ---------- Start ---------- */

  const saved = readConsent();
  if (saved) {
    applyConsent(saved); // API bei jedem Seitenaufruf mit gespeicherter Wahl versorgen
    fabFreigeben();
  } else {
    // Nach dem kritischen Render einblenden, aber vor jeder Interaktion
    requestAnimationFrame(() => requestAnimationFrame(openBanner));
  }
})();
